import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Select,
  Toolbar,
  Tooltip,
  Typography,
  SelectChangeEvent,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import avatarImage from '../../assets/images/avatar.png';
import { useQueryParam } from '../../hooks/useQueryParams';
import {
  getCommunityAppBarColor,
  getCommunityLogo,
  getCommunityPalette,
  getContrastTextColor,
} from '../../theme';

const settings = ['Logout'];

const ROUTE_OPTIONS = [
  { path: '/chatbot', label: 'ChatBot' },
  { path: '/translator', label: 'Translator' },
];

const COMMUNITY_TO_HOMEPAGE_MAP: Record<string, { name: string; url: string }> =
  {
    fetisch: { name: 'Fetisch', url: 'https://fetisch.de' },
    fetish: { name: 'Fetish', url: 'https://www.fetish.com' },
    fet: { name: 'Fetish', url: 'https://www.fetish.com/' },
    popcorn: { name: 'Popcorn', url: 'https://popcorn.dating' },
    poppen: { name: 'Poppen', url: 'https://poppen.de' },
    'popcorn.dating': {
      name: 'Popcorn',
      url: 'https://www.popcorn-dating.com',
    },
    gayscom: { name: 'GaysCom', url: 'https://gays.com' },
    gayde: { name: 'GaysCom', url: 'https://gay.de' },
    kaufmich: { name: 'Kaufmich', url: 'https://www.kaufmich.com' },
  };

export default function Header() {
  const { i18n } = useTranslation();
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const showLayout = useQueryParam('show_layout');
  const specificTheme = useQueryParam('specific_theme') || 'true';

  // logic to determine which header to show
  const shouldShowLogoOnlyHeader =
    showLayout !== 'true' && specificTheme === 'true';
  const shouldShowNoHeader = showLayout !== 'true' && specificTheme !== 'true';

  if (shouldShowNoHeader) {
    return null;
  }

  const currentPath = window.location.pathname;
  const pathSegments = currentPath.split('/').filter(Boolean);
  // URL: /support/chatbot/:lang/:community/:session_id
  const community = pathSegments[3];

  const communityLogo = getCommunityLogo(community);
  const appBarColor = getCommunityAppBarColor(community);
  const palette = getCommunityPalette(community);

  const getLogoSrc = () => {
    if (specificTheme === 'true' && communityLogo) {
      try {
        return require(`../../assets/communityLogos/${communityLogo}`);
      } catch (error) {
        console.warn(`Community logo not found: ${communityLogo}`);
        return null;
      }
    }
    return null;
  };

  const logoSrc = getLogoSrc();

  const contrastText = getContrastTextColor(community);
  const headerTextColor = contrastText || 'white';

  const handleLanguageChange = (event: SelectChangeEvent) => {
    const newLang = event.target.value;
    const oldLang = params.lang;
    const newPath = location.pathname.replace(`/${oldLang}`, `/${newLang}`);

    const searchParams = new URLSearchParams(location.search);

    const queryString = searchParams.toString();
    const fullPath = queryString ? `${newPath}?${queryString}` : newPath;

    i18n.changeLanguage(newLang);
    navigate(fullPath);
  };

  const handleRouteChange = (event: SelectChangeEvent) => {
    const newRoute = event.target.value;

    const searchParams = new URLSearchParams(location.search);

    const queryString = searchParams.toString();
    const fullPath = queryString
      ? `${newRoute}/${i18n.language}?${queryString}`
      : `${newRoute}/${i18n.language}`;

    navigate(fullPath);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="static"
        elevation={palette ? 0 : 4}
        sx={{
          ...(appBarColor
            ? { backgroundColor: appBarColor, color: headerTextColor }
            : {}),
        }}
      >
        <Toolbar>
          {shouldShowLogoOnlyHeader ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'flex-start',
              }}
            >
              {logoSrc ? (
                <Link
                  href={COMMUNITY_TO_HOMEPAGE_MAP[community]?.url}
                  rel="noopener noreferrer"
                  underline="none"
                  color="inherit"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <img
                      src={logoSrc}
                      alt={`${community} logo`}
                      style={{
                        height: '40px',
                        maxWidth: '180px',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                </Link>
              ) : (
                <Typography variant="h6" component="div">
                  EasySupport
                </Typography>
              )}
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexGrow: 1,
                  alignItems: 'center',
                }}
              >
                {logoSrc ? (
                  <img
                    src={logoSrc}
                    alt={`${community} logo`}
                    style={{
                      height: '32px',
                      maxWidth: '120px',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <Typography variant="h6" component="div">
                    EasySupport
                  </Typography>
                )}
                <Select
                  value={window.location.href.match(/\/support(\/[^/]+)/)?.[1]}
                  onChange={handleRouteChange}
                  variant="standard"
                  disableUnderline
                  IconComponent={() => null}
                  sx={{
                    ml: 3,
                    '& .MuiSelect-select': {
                      paddingRight: '12px !important',
                      color: headerTextColor,
                      fontWeight: 500,
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'action.active',
                      right: 0,
                    },
                  }}
                >
                  {ROUTE_OPTIONS.map((option) => (
                    <MenuItem key={option.path} value={option.path}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <span className="material-symbols-outlined">expand_more</span>
              </Box>

              <Box
                sx={{
                  ml: 2,
                  position: 'relative',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: '24px',
                  height: '24px',
                }}
              >
                <Select
                  value={i18n.language}
                  onChange={handleLanguageChange}
                  variant="standard"
                  disableUnderline
                  IconComponent={() => null}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                    '& .MuiSelect-select': {
                      padding: 0,
                      minHeight: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                  }}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="de">Deutsch</MenuItem>
                </Select>
                <span
                  className="material-symbols-outlined"
                  style={{
                    color: headerTextColor,
                    fontSize: '20px',
                    pointerEvents: 'none',
                  }}
                >
                  translate
                </span>
              </Box>

              {false && (
                <Box sx={{ flexGrow: 0 }}>
                  <Tooltip title="Account settings">
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                      <Avatar alt="avator" src={avatarImage} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    sx={{ mt: '45px' }}
                    id="menu-appbar"
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                  >
                    {settings.map((setting) => (
                      <MenuItem key={setting} onClick={handleCloseUserMenu}>
                        <Typography sx={{ textAlign: 'center' }}>
                          {setting}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              )}
            </>
          )}
        </Toolbar>
      </AppBar>
    </Box>
  );
}
